import React, { useState, useRef } from "react";
import useEffectOnce from "../../useEffectOnce";
import "./Publisher.scss";
import { Radio, RadioGroup, FormControlLabel } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LogicModal from "../../Components/LogicModal/LogicModal";
import AiConfidenceModal from "../../Components/AiConfidenceModal/AiConfidenceModal";
import { serverAddr } from "../../networkConfig";
import SearchCoin from "../../Components/SearchCoin/SearchCoin";


const calculateEntryRange = (entry, opinion) => {
  if (!entry) return { min: "", max: "" };

  const percent = 0.005; // 0.5%

  if (opinion === "long") {
    return {
      min: (entry * (1 - percent)).toFixed(2),
      max: (entry * (1 + percent)).toFixed(2)
    };
  } else {
    return {
      min: (entry * (1 + percent)).toFixed(2),
      max: (entry * (1 - percent)).toFixed(2)
    };
  }
};

function Card({ coin, setCoins, index }) {
  const [opinion, setOpinion] = useState("long");
  const [entryType, setEntryType] = useState("market");

  const [entry, setEntry] = useState("");
  const [target1, setTarget1] = useState("");
  const [target2, setTarget2] = useState("");
  const [stoploss, setStoploss] = useState("");
  const [leverage, setLeverage] = useState("");
  const [markPrice, setMarkPrice] = useState("");

  const [validity, setValidity] = useState("");
  const [openLogic, setOpenLogic] = useState(false);
  const [openAiScore, setOpenAiScore] = useState(false);
  const [errors, setErrors] = useState({});
  const dateInputRef = useRef(null);

  const e = Number(entry);
  const mp = Number(markPrice);

  const { min: entryRangeMin, max: entryRangeMax } =
    calculateEntryRange(Number(entry), opinion);

  useEffectOnce(() => {
    const result = validateAll({
      entry: Number(entry),
      markPrice: Number(coin.markPrice),
      stoploss: Number(stoploss),
      target1: Number(target1),
      target2: Number(target2),
      entryMin: Number(entryRangeMin),
      entryMax: Number(entryRangeMax),
      opinion
    });

    setErrors(result.errors);

  }, [entry, stoploss, target1, target2, opinion, coin.markPrice]);

  // RR calculation
  const calculateRR = (entry, stoploss, target1, opinion) => {
    const risk = Math.abs(entry - stoploss);
    if (risk === 0) return 0;

    if (opinion === "long") {
      return (target1 - entry) / risk;
    } else {
      return (entry - target1) / risk;
    }
  };

  // Entry validation (±10%)
  const validateEntry = (entry, markPrice) => {
    const min = markPrice * 0.9;
    const max = markPrice * 1.1;

    return entry >= min && entry <= max;
  };

  // Stoploss validation
  const validateStoploss = (entry, stoploss, opinion) => {
    const percent = Math.abs((entry - stoploss) / entry) * 100;

    if (percent > 20) return false;

    if (opinion === "long") return stoploss < entry;
    else return stoploss > entry;
  };

  // Target validation
  const validateTargets = (entry, stoploss, t1, t2, opinion) => {
    const risk = Math.abs(entry - stoploss);

    if (risk <= 0) return false;

    if (opinion === "long") {
      return (
        t1 >= entry + risk * 1.5 &&
        t2 >= entry + risk * 2
      );
    } else {
      return (
        t1 <= entry - risk * 1.5 &&
        t2 <= entry - risk * 2
      );
    }
  };


  const validateAll = ({
    entry,
    markPrice,
    stoploss,
    target1,
    target2,
    opinion,
    entryMin,
    entryMax
  }) => {
    const errors = {};

    if (!entry || !stoploss || !target1 || !target2) {
      errors.general = "All fields are required";
      return { errors, final: "REJECTED" };
    }

    // ENTRY VALIDATION (±10%)
    const minEntry = markPrice * 0.9;
    const maxEntry = markPrice * 1.1;

    if (entry < minEntry || entry > maxEntry) {
      errors.entry = `Entry must be between ${minEntry.toFixed(2)} - ${maxEntry.toFixed(2)}`;
    }

    // STOPLOSS VALIDATION
    const slPercent = Math.abs((entry - stoploss) / entry) * 100;

    if (slPercent > 20) {
      errors.stoploss = "Stoploss must be within 20% of entry";
    }

    if (opinion === "long" && stoploss >= entry) {
      errors.stoploss = "Stoploss must be below entry (LONG)";
    }

    if (opinion === "short" && stoploss <= entry) {
      errors.stoploss = "Stoploss must be above entry (SHORT)";
    }

    // RR CALCULATION
    const rr = calculateRR(entry, stoploss, target1, opinion);

    // ENTRY RANGE %
    const rangePercent = ((entryMax - entryMin) / entry) * 100;
    const maxAllowed = Math.min(1, 5 * rr);

    if (rangePercent < 0.5 || rangePercent > maxAllowed) {
      errors.entryRange = "Entry range must be 0.5% – allowed RR range";
    }

    // TARGET VALIDATION
    const risk = Math.abs(entry - stoploss);

    if (opinion === "long") {
      if (target1 < entry + risk * 1.5) {
        errors.target1 = "Target1 invalid (RR issue)";
      }
      if (target2 < entry + risk * 2) {
        errors.target2 = "Target2 invalid (RR issue)";
      }
    } else {
      if (target1 > entry - risk * 1.5) {
        errors.target1 = "Target1 invalid (RR issue)";
      }
      if (target2 > entry - risk * 2) {
        errors.target2 = "Target2 invalid (RR issue)";
      }
    }

    // DIRECTION VALIDATION
    if (opinion === "long") {
      if (!(stoploss < entry && target1 > entry)) {
        errors.direction = "Direction mismatch";
      }
    } else {
      if (!(stoploss > entry && target1 < entry)) {
        errors.direction = "Direction mismatch";
      }
    }

    return {
      errors,
      final: Object.keys(errors).length === 0 ? "APPROVED" : "REJECTED",
      rr
    };
  };

  useEffectOnce(() => {
    const e = Number(entry);
    const mp = Number(coin.markPrice);
    const sl = Number(stoploss);
    const t1 = Number(target1);
    const t2 = Number(target2);

    const newErrors = {};

    // Only validate when value exists
    if (e && mp && !validateEntry(e, mp)) {
      newErrors.entry = "Entry must be within ±10% of mark price";
    }

    if (e && sl && !validateStoploss(e, sl, opinion)) {
      newErrors.stoploss = "Invalid stoploss";
    }

    if (e && sl && t1 && t2 && !validateTargets(e, sl, t1, t2, opinion)) {
      newErrors.target1 = "Targets not matching RR";
      newErrors.target2 = "Targets not matching RR";
    }

    setErrors((prev) => ({
      ...prev,
      ...newErrors
    }));

  }, [entry, stoploss, target1, target2, opinion, coin.markPrice]);

  const handleLogicClick = () => {
    const e = Number(entry);
    const mp = Number(coin.markPrice);
    const sl = Number(stoploss);
    const t1 = Number(target1);
    const t2 = Number(target2);
    const lev = Number(leverage);

    const newErrors = {};

    // Required fields ONLY here
    if (!e || !sl || !t1 || !t2 || !lev) {
      newErrors.general = "All fields are required";
      setErrors(newErrors);
      return;
    }

    // Run full validation
    if (!validateEntry(e, mp)) {
      newErrors.entry = "Invalid Entry";
    }

    if (!validateStoploss(e, sl, opinion)) {
      newErrors.stoploss = "Invalid Stoploss";
    }

    if (!validateTargets(e, sl, t1, t2, opinion)) {
      newErrors.target1 = "Invalid Targets";
      newErrors.target2 = "Invalid Targets";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setOpenLogic(true);
  };


  return (
    <div className="publisher-card">

      <div className="form-group col-12">
        <label>Mark Price (For Testing Only) *</label>
        <input
          className="entry-input"
          placeholder="Enter Mark Price"
          value={coin.markPrice || ""}
          onChange={(e) => {
            const value = e.target.value;

            setCoins((prev) => {
              const updated = [...prev];
              updated[index] = {
                ...updated[index],
                markPrice: value
              };
              return updated;
            });
          }}
        />


      </div>
      <div className="card-header">
        <div className="coin-name">{coin.name}</div>

        <div className="price">
          ${coin.price}
          <span className="green"> ▲{coin.change}</span>
        </div>

        <div className="date">{coin.date}</div>
      </div>

      <div className="card-divider"></div>

      <div className="row g-2">

        {/* Row 1: My Opinion | Entry Price */}
        <div className="col-12 col-sm-5 form-group">
          <label>My Opinion *</label>
          <div className="toggle">
            <button
              className={opinion === "long" ? "active" : ""}
              onClick={() => setOpinion("long")}
            >
              Long
            </button>
            <button
              className={opinion === "short" ? "active" : ""}
              onClick={() => setOpinion("short")}
            >
              Short
            </button>
          </div>
        </div>

        <div className="col-12 col-sm-7 form-group">
          <label>Entry Price *</label>
          <div className="entry-row">
            <input
              className="entry-price-input"
              placeholder="LTP"
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
            />
            <RadioGroup
              row
              value={entryType}
              onChange={(e) => setEntryType(e.target.value)}
              className="entry-radio"
            >
              <FormControlLabel
                value="market"
                control={<Radio size="small" />}
                label="Market"
              />
              <FormControlLabel
                value="limit"
                control={<Radio size="small" />}
                label="Limit"
              />
            </RadioGroup>
          </div>
          <div className="sub-text">Must be within ±10% of mark price</div>
          {errors.entry && <div className="error-text">{errors.entry}</div>}
        </div>

        {/* Row 2: Validity | Entry Range */}
        <div className="col-12 col-sm-5 form-group">
          <label>Validity *</label>
          <div className="date-input-wrap" onClick={() => dateInputRef.current?.showPicker()}>
            <span className={`date-placeholder ${validity ? "hidden" : ""}`}></span>
            <input
              ref={dateInputRef}
              type="date"
              value={validity}
              onChange={(e) => setValidity(e.target.value)}
            />
            <CalendarTodayIcon className="date-icon" />
          </div>
        </div>

        <div className="col-12 col-sm-7 form-group">
          <label>Entry Range</label>
          <div className="range-row">
            <div className="input-box">
              <span className="input-box__prefix">₹</span>
              <input className="cursor-not-allowed" readOnly value={entryRangeMin} placeholder="100" />
            </div>
            <div className="input-box">
              <span className="input-box__prefix">₹</span>
              <input className="cursor-not-allowed" readOnly value={entryRangeMax} placeholder="90" />
            </div>
          </div>
          <span className="sub-text">0.5% – 1% tight range</span>
        </div>

        {/* Row 3: Leverage | Target 1 | Target 2 */}
        <div className="col-12 col-sm-5 form-group">
          <label>Leverage *</label>
          <input
            placeholder="0X"
            value={leverage}
            onChange={(e) => setLeverage(e.target.value)}
          />
          {errors.leverage && <div className="error-text">{errors.leverage}</div>}
          <span className="sub-text">Maintain leverage above 2x</span>
        </div>
        <div className="col-12 col-sm-7 form-group">
          <div className="row g-2">
            <div className="col-6 col-sm-6 form-group">
              <label>Target 1 *</label>
              <div className="input-box">
                <span className="input-box__prefix">₹</span>
                <input
                  placeholder="100"
                  value={target1}
                  onChange={(e) => setTarget1(e.target.value)}
                />
                <span className="input-box__suffix">0.01%</span>
              </div>
              <span className="sub-text">RR- 1:1; min. 5%</span>
              {errors.target1 && <div className="error-text">{errors.target1}</div>}
            </div>

            <div className="col-6 col-sm-6 form-group">
              <label>Target 2 *</label>
              <div className="input-box">
                <span className="input-box__prefix">₹</span>
                <input
                  placeholder="90"
                  value={target2}
                  onChange={(e) => setTarget2(e.target.value)}
                />
                <span className="input-box__suffix">0.01%</span>
              </div>
              <span className="sub-text">RR-1.5:1; min. 7.5%</span>
              {errors.target2 && <div className="error-text">{errors.target2}</div>}
            </div>
          </div>
        </div>

        {/* Row 4: Stoploss full width */}
        <div className="col-12 form-group">
          <label>Stoploss *</label>
          <div className="input-box">
            <span className="input-box__prefix">₹</span>
            <input
              placeholder="200"
              value={stoploss}
              onChange={(e) => setStoploss(e.target.value)}
            />
            <span className="input-box__suffix">0.01%</span>
          </div>
          <span className="sub-text">Must be within ±20% of Entry</span>
          {errors.stoploss && <div className="error-text">{errors.stoploss}</div>}
          {errors.stoplossRange && <div className="error-text">{errors.stoplossRange}</div>}
        </div>

      </div>

      <div className="footer-info">
        Scalping, defined as a very short-duration trade setup designed
        to capture small price movements within minutes to a few hours,
        typically targeting low percentage gains with tight stop-loss
        levels, is strictly not permitted on CryptoAnts
      </div>

      {errors.general && (
        <div className="error-text" style={{ textAlign: "center", marginTop: '20px', marginBottom: "10px" }}>
          {errors.general}
        </div>
      )}
      <div className="card-footer">
        <button className="ai-score-btn" onClick={() => setOpenAiScore(true)}>AI Confidence Score</button>
        {/* onClick={() => setOpenLogic(true)} */}
        <button onClick={handleLogicClick}>LOGIC</button>
      </div>
      {/* onClose(); */}

      <LogicModal open={openLogic} onClose={() => setOpenLogic(false)} />
      <AiConfidenceModal
        open={openAiScore}
        onClose={() => setOpenAiScore(false)}
        onAddLogic={() => { setOpenAiScore(false); setOpenLogic(true); }}
        score={coin.confidenceScore}
      />
    </div>
  );
}

export default function Publisher() {
  const [coinsData, setCoins] = useState([]);
  const [loading, setLoading] = useState(false);
  // const [search, setSearch] = useState("");


  const handleSearchResult = (coin) => {
    setCoins((prev) => {
      // remove duplicate if already exists
      const filtered = prev.filter((c) => c.name !== coin.name);

      // add searched coin at top
      return [coin, ...filtered];
    });
  };

  useEffectOnce(() => {
    fetchCoins();
  }, []);

  const fetchCoins = async () => {
    try {
      setLoading(true);

      const response = await serverAddr.post("/publisher/get_reco_details/", {
        page_no: 1,
        page_per_entries: 6
      });

      const data = response?.data || [];

      // map API data to your UI format
      const formatted = data.map((item) => {
        return {
          name: item.title || '',
          price: item.price,
          change: item.price_change,
          date: item.timestamp,
          confidenceScore: item.confidence_score || 2,
          markPrice: null
        };
      });

      setCoins(formatted);

    } catch (error) {
      console.error("Error fetching coins:", error);
    } finally {
      setLoading(false);
    }
  };


  // const filteredCoins = coinsData.filter((coin) =>
  //   coin.name.toLowerCase().includes(search.toLowerCase())
  // );

  return (
    <div className="publisher-wrapper">
      {/* SEARCH BAR */}
      <div className="search-bar">
        <SearchCoin onResult={handleSearchResult} />
      </div>

      <div className="publisher-container">
        {coinsData.map((coin, index) => (
          <Card key={index} coin={coin} setCoins={setCoins} index={index} />
        ))}
      </div>
    </div>
  );
}